/**
 * HTML 转 Markdown 工具函数
 * 将 HTML 富文本转换为 Markdown 格式
 */

/**
 * 将 HTML 转换为 Markdown
 * @param {string} html - HTML 字符串
 * @returns {string} Markdown 字符串
 */
export function htmlToMarkdown(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }

  let markdown = html;

  // 处理图片标签 <img src="..." .../> 或 < img src="..." .../>
  // 注意：可能包含转义的空格，如 < img
  markdown = markdown.replace(/<\s*img\s+[^>]*src\s*=\s*["']([^"']+)["'][^>]*\/?>/gi, (match, src) => {
    // 提取 alt 属性（如果有）
    const altMatch = match.match(/alt\s*=\s*["']([^"']+)["']/i);
    const alt = altMatch ? altMatch[1] : '';
    return `![${alt}](${src})`;
  });

  // 处理 font 标签，提取内容（保留文本，忽略样式）
  // 先处理 font 标签，因为它可能嵌套在其他标签中
  markdown = markdown.replace(/<\s*font[^>]*>([\s\S]*?)<\s*\/font\s*>/gi, '$1');

  // 处理粗体标签 <b>...</b> 或 <strong>...</strong>
  markdown = markdown.replace(/<\s*(?:b|strong)\s*>([\s\S]*?)<\s*\/(?:b|strong)\s*>/gi, '**$1**');

  // 处理斜体标签 <i>...</i> 或 <em>...</em>
  markdown = markdown.replace(/<\s*(?:i|em)\s*>([\s\S]*?)<\s*\/(?:i|em)\s*>/gi, '*$1*');

  // 处理段落标签 <p>...</p>，转换为换行
  markdown = markdown.replace(/<\s*p\s*[^>]*>([\s\S]*?)<\s*\/p\s*>/gi, (match, content) => {
    const trimmedContent = content.trim();
    return trimmedContent ? `\n\n${trimmedContent}\n\n` : '\n\n';
  });

  // 处理换行标签 <br/> 或 <br>
  markdown = markdown.replace(/<\s*br\s*\/?\s*>/gi, '\n');

  // 处理其他常见标签，提取文本内容
  // 处理标题标签
  markdown = markdown.replace(/<\s*h([1-6])\s*[^>]*>([\s\S]*?)<\s*\/h[1-6]\s*>/gi, (match, level, content) => {
    const prefix = '#'.repeat(parseInt(level));
    return `\n\n${prefix} ${content.trim()}\n\n`;
  });

  // 处理列表标签
  markdown = markdown.replace(/<\s*ul\s*[^>]*>([\s\S]*?)<\s*\/ul\s*>/gi, '\n$1\n');
  markdown = markdown.replace(/<\s*ol\s*[^>]*>([\s\S]*?)<\s*\/ol\s*>/gi, '\n$1\n');
  markdown = markdown.replace(/<\s*li\s*[^>]*>([\s\S]*?)<\s*\/li\s*>/gi, '- $1\n');

  // 处理链接标签 <a href="...">...</a>
  markdown = markdown.replace(/<\s*a\s+[^>]*href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\s*\/a\s*>/gi, '[$2]($1)');

  // 移除所有剩余的 HTML 标签，只保留文本内容
  markdown = markdown.replace(/<[^>]+>/g, '');

  // 解码 HTML 实体
  markdown = markdown
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'");

  // 清理多余的空白字符和换行
  markdown = markdown
    .replace(/\n{3,}/g, '\n\n') // 多个连续换行合并为两个
    .replace(/[ \t]+/g, ' ') // 多个空格合并为一个
    .trim();

  return markdown;
}
